// import { morph } from './morph.js';
// import { Component } from './component.js';
// import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from './events.js';
// import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
// import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from './utilities.js';
// import VariantPicker from '@theme/variant-picker';
import { morph } from './morph.js';
import { Component } from './component.js';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from './events.js';
import { DialogComponent, DialogCloseEvent } from './dialog.js';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from './utilities.js';
import VariantPicker from './variant-picker.js';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  // HOOYI_QUICK_ADD_INSTANT_MODAL_START
  // 作用：防止用户连续点击多个商品时，旧请求回来覆盖新弹窗内容。
  // 还原：删除本字段，并删除所有 HOOYI_QUICK_ADD_INSTANT_MODAL 相关代码。
  #hoooyiQuickAddToken = 0;
  // HOOYI_QUICK_ADD_INSTANT_MODAL_END
  // HOOYI_QUICK_ADD_PREFETCH_START
  // 作用：缓存正在预加载的 quick add 内容，避免重复请求。
  // 还原：删除本字段，并删除所有 HOOYI_QUICK_ADD_PREFETCH 相关代码。
  /** @type {Map<string, Promise<Element | null>>} */
  #prefetchPromises = new Map();
  // HOOYI_QUICK_ADD_PREFETCH_END
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    // HOOYI_QUICK_ADD_PREFETCH_START
    // 作用：
    // 1. 鼠标移到商品卡 quick add 区域时，提前请求 quick add 弹窗内容；
    // 2. 键盘 focus 或移动端 touchstart 时，也提前请求；
    // 3. 用户真正点击 Choose 时，优先用缓存内容，减少等待。
    //
    // 还原：
    // 删除这三行监听，并删除 disconnectedCallback 里的对应 removeEventListener。
    this.addEventListener('pointerenter', this.#prefetchQuickAddContent);
    this.addEventListener('focusin', this.#prefetchQuickAddContent);
    this.addEventListener('touchstart', this.#prefetchQuickAddContent, { passive: true });
    // HOOYI_QUICK_ADD_PREFETCH_END
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    // HOOYI_QUICK_ADD_PREFETCH_START
    // 作用：组件销毁时移除 quick add 预加载监听。
    // 还原：删除这三行。
    this.removeEventListener('pointerenter', this.#prefetchQuickAddContent);
    this.removeEventListener('focusin', this.#prefetchQuickAddContent);
    this.removeEventListener('touchstart', this.#prefetchQuickAddContent);
    // HOOYI_QUICK_ADD_PREFETCH_END
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  // HOOYI_QUICK_ADD_INSTANT_MODAL_START
  /**
   * Shows loading state immediately inside quick add modal.
   *
   * 作用：
   * 点击 Choose 后立即打开弹窗，并让 Loading 绝对居中显示。
   *
   * 还原：
   * 删除本方法，并恢复原主题“请求完成后再打开弹窗”的 handleClick 逻辑。
   *
   * @param {number} token
   */
  #showQuickAddLoading(token) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    modalContent.dataset.hoooyiQuickAddToken = String(token);
    modalContent.setAttribute('data-hoooyi-quick-add-loading', 'true');

    const dialog = modalContent.closest('quick-add-dialog, dialog');
    if (dialog) {
      dialog.setAttribute('data-hoooyi-quick-add-loading-dialog', 'true');
    }

    modalContent.innerHTML = `
      <div class="hoooyi-quick-add-loading" role="status" aria-live="polite">
        <div class="hoooyi-quick-add-loading__inner">
          <div class="hoooyi-quick-add-loading__spinner"></div>
          <div class="hoooyi-quick-add-loading__text">Loading...</div>
        </div>
      </div>
    `;
  }

  /**
   * Shows error state if quick add content fails to load.
   *
   * @param {number} token
   */
  #showQuickAddError(token) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!modalContent) return;
    if (modalContent.dataset.hoooyiQuickAddToken !== String(token)) return;

    modalContent.innerHTML = `
      <div class="hoooyi-quick-add-loading" role="alert">
        <div class="hoooyi-quick-add-loading__text">Unable to load product. Please try again.</div>
      </div>
    `;
  }

  /**
   * Checks whether current async request still belongs to the currently opened quick add modal.
   *
   * @param {number} token
   * @returns {boolean}
   */
  #isQuickAddTokenActive(token) {
    const modalContent = document.getElementById('quick-add-modal-content');
    return modalContent?.dataset.hoooyiQuickAddToken === String(token);
  }
  // HOOYI_QUICK_ADD_INSTANT_MODAL_END

  // HOOYI_QUICK_ADD_PREFETCH_START
 
  /**
   * Gets product-information section id from current product page.
   *
   * 重要：
   * 只能在商品详情页拿 product section id。
   * 不要在 collection/list 页面用 [id^="shopify-section-template--"][id$="__main"] 兜底，
   * 因为那会拿到集合页 main section id，导致：
   * /products/xxx?section_id=collection_main
   * 返回内容里没有 [data-product-grid-content]，最终 Quick add 报 Unable to load product。
   *
   * 还原：
   * 删除本方法，并恢复 fetchProductPage() 直接 fetch(productPageUrl)。
   *
   * @returns {string | null}
   */
  #getQuickAddProductSectionId() {
    const isProductPage = window.location.pathname.includes('/products/');

    if (!isProductPage) {
      return null;
    }

    const currentProductPicker = document.querySelector(
      'main variant-picker[data-template-product-match="true"]'
    );

    if (currentProductPicker instanceof HTMLElement && currentProductPicker.dataset.sectionId) {
      return currentProductPicker.dataset.sectionId;
    }

    return null;
  }

  /**
   * Builds quick add fetch URL.
   *
   * 规则：
   * 1. 商品详情页里：可以用当前商品详情页 section_id 提速；
   * 2. 集合页/列表页里：不要乱加 section_id，直接请求商品页，避免拿错 collection section。
   *
   * @param {string} productPageUrl
   * @returns {string}
   */
  #buildQuickAddFetchUrl(productPageUrl) {
    const url = new URL(productPageUrl, window.location.origin);
    const sectionId = this.#getQuickAddProductSectionId();

    if (sectionId) {
      url.searchParams.set('section_id', sectionId);
    } else {
      url.searchParams.delete('section_id');
    }

    return url.toString();
  }

  /**
   * Extracts and caches quick add modal content.
   *
   * @param {Document | null} html
   * @param {string} cacheKey
   * @returns {Element | null}
   */
  #cacheQuickAddProductGrid(html, cacheKey) {
    if (!html) return null;

    const gridElement = html.querySelector('[data-product-grid-content]');

    if (!gridElement) return null;

    const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));

    this.#cachedContent.set(cacheKey, productGrid);

    return productGrid;
  }

  /**
   * Prefetches quick add content before user clicks.
   *
   * 触发时机：
   * - pointerenter
   * - focusin
   * - touchstart
   *
   * @returns {Promise<Element | null>}
   */
  #prefetchQuickAddContent = () => {
    const currentUrl = this.productPageUrl;

    if (!currentUrl) return Promise.resolve(null);

    if (this.#cachedContent.has(currentUrl)) {
      return Promise.resolve(this.#cachedContent.get(currentUrl) || null);
    }

    if (this.#prefetchPromises.has(currentUrl)) {
      return this.#prefetchPromises.get(currentUrl) || Promise.resolve(null);
    }

    const promise = this.fetchProductPage(currentUrl)
      .then((html) => {
        return this.#cacheQuickAddProductGrid(html, currentUrl);
      })
      .catch((error) => {
        console.warn('[HOOYI_QUICK_ADD_PREFETCH] prefetch failed', error);
        return null;
      })
      .finally(() => {
        this.#prefetchPromises.delete(currentUrl);
      });

    this.#prefetchPromises.set(currentUrl, promise);

    return promise;
  };
  // HOOYI_QUICK_ADD_PREFETCH_END

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (!currentUrl) return;

    // HOOYI_QUICK_ADD_INSTANT_MODAL_START
    // 作用：
    // 1. 点击 Choose 后立即打开弹窗；
    // 2. 如果内容已缓存，则直接渲染内容；
    // 3. 如果内容还没加载完，则先显示 Loading，再等请求回来替换内容；
    // 4. 用 token 防止旧请求覆盖新弹窗。
    //
    // 还原：
    // 删除本段，恢复原主题“fetch 完成后再 #openQuickAddModal()”的逻辑。
    const token = ++this.#hoooyiQuickAddToken;

    let productGrid = this.#cachedContent.get(currentUrl);

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
      this.#openQuickAddModal();
      return;
    }

    this.#showQuickAddLoading(token);
    this.#openQuickAddModal();

    try {
      const pendingPrefetch = this.#prefetchPromises?.get(currentUrl);

      if (pendingPrefetch) {
        productGrid = await pendingPrefetch;
      } else {
        const html = await this.fetchProductPage(currentUrl);
        productGrid = this.#cacheQuickAddProductGrid(html, currentUrl);
      }

      if (!this.#isQuickAddTokenActive(token)) return;

      if (productGrid) {
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);
      } else {
        this.#showQuickAddError(token);
      }
    } catch (error) {
      console.error('[HOOYI_QUICK_ADD_INSTANT_MODAL] load failed', error);

      if (this.#isQuickAddTokenActive(token)) {
        this.#showQuickAddError(token);
      }
    }
    // HOOYI_QUICK_ADD_INSTANT_MODAL_END
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      // HOOYI_QUICK_ADD_SECTION_FETCH_START
      // 作用：
      // 原主题 fetch(productPageUrl) 会请求完整商品详情页。
      // 这里改成优先请求 product-information section：
      // /products/xxx?section_id=template--xxx__main
      //
      // 如果当前页面拿不到 product section id，会自动回退为完整商品页 URL。
      //
      // 还原：
      // 把 fetchUrl 替换回 productPageUrl。
      const fetchUrl = this.#buildQuickAddFetchUrl(productPageUrl);
      // HOOYI_QUICK_ADD_SECTION_FETCH_END

      const response = await fetch(fetchUrl, {
        signal: this.#abortController.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    // HOOYI_QUICK_ADD_INSTANT_MODAL_START
    // 作用：商品内容加载完成后，清除 loading 布局状态。
    // 还原：删除本段。
    modalContent.removeAttribute('data-hoooyi-quick-add-loading');

    const dialog = modalContent.closest('quick-add-dialog, dialog');
    if (dialog) {
      dialog.removeAttribute('data-hoooyi-quick-add-loading-dialog');
    }
    // HOOYI_QUICK_ADD_INSTANT_MODAL_END

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
